import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import {
  BackButton, DataTable, Modal, SearchFilterBar, TabStrip, TruncatedValue,
  type ColumnDef, type FilterDef, type SortState,
} from '@shared-ui/components';
import { type UserSearchOption } from '@shared-ui/user-picker';
import { ScopedUserSearchFilter } from '../../components/ScopedUserSearchFilter';
import { adminApi } from '../../api/adminApi';
import { formatBytes, formatWhen, type BackupRow } from './driveBackupFormat';
import { BackupStateBadge } from './BackupStateBadge';

type Delivery = Awaited<ReturnType<typeof adminApi.getDriveBackupDeliveries>>['deliveries'][number];

type DetailView = 'table' | 'folders';

const VIEW_TABS = [
  { key: 'table' as const, label: 'All files' },
  { key: 'folders' as const, label: 'Folders' },
];

/** Deliveries before 23 Sep 2026 recorded no folder path (or source blob);
 *  prod's were backfilled (scripts/ops/backfillDriveDeliveryPaths.ts), but an
 *  environment that was not still has them. They did go to a real folder — we
 *  just did not write down which. Grouping them under the bare category key
 *  (`work`) read as a folder that exists in their Drive, so they get a
 *  pseudo-folder that says what it is instead. */
const UNRECORDED_FOLDER = 'Folder not recorded';
const CATEGORY_FOLDER_NAMES: Record<string, string> = {
  uploads: 'Papers I uploaded',
  work: 'My work',
  generated: 'What BuzzBrain made',
};

function folderOf(d: Delivery): string {
  return d.driveFolderPath
    || `${UNRECORDED_FOLDER}/${CATEGORY_FOLDER_NAMES[d.category] ?? d.category}`;
}

/** `MM/DD HH:mm`, local time — compact enough to sit on one line in a table
 *  with eight columns. 24-hour, since without AM/PM a 12-hour clock is
 *  ambiguous. Missing is "—", not `formatWhen`'s "Never": for an upload time
 *  it means the blob is gone, not that nothing was uploaded. */
function shortWhen(iso?: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  const two = (n: number) => String(n).padStart(2, '0');
  return `${two(d.getMonth() + 1)}/${two(d.getDate())} ${two(d.getHours())}:${two(d.getMinutes())}`;
}

function uploadedWhen(d: Delivery): string {
  return shortWhen(d.blobCreatedAt);
}

/** At most 20 characters of a blob path — its tail, which is what tells two
 *  apart. The whole path is the hover (TruncatedValue's title) or a click to copy. */
const BLOB_CHARS = 20;
function blobTail(path: string): string {
  return path.length <= BLOB_CHARS ? path : `…${path.slice(-(BLOB_CHARS - 1))}`;
}

function splitPath(path: string): string[] {
  return path.split('/').filter(Boolean);
}

/**
 * One connection, full width, in place of the list.
 *
 * Replaces an inline expansion that nested a second table inside a row of the
 * first — five wide columns squeezed under seven, for a person with a
 * thousand-plus files. Here the deliveries get the whole tab, and two ways to
 * read them: every file as one sortable table, or the folder structure as it
 * should look in their Drive, rebuilt from our own delivery records.
 *
 * The folder view is OUR record of what we wrote, not a listing of their Drive:
 * we cannot read it, and a folder they renamed or a file they deleted will not
 * show here. That is the right thing for this screen — the question it answers
 * is "what did we deliver, and where did we put it".
 */
export function DriveBackupDetail({ backup, ownerId, onBack }: {
  /** The list row, when the list has loaded; the header degrades to the id without it. */
  backup?: BackupRow;
  ownerId: string;
  onBack: () => void;
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const view: DetailView = searchParams.get('backupView') === 'folders' ? 'folders' : 'table';
  const folderPath = searchParams.get('backupPath') ?? '';

  const setParam = (key: string, value: string, replace = false) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (value) next.set(key, value); else next.delete(key);
      return next;
    }, { replace });
  };

  const { data, isLoading, error } = useQuery({
    queryKey: ['driveBackupDeliveries', ownerId, NO_FILTERS],
    queryFn: () => adminApi.getDriveBackupDeliveries(ownerId),
  });
  const deliveries = useMemo(() => data?.deliveries ?? [], [data]);
  const [previewing, setPreviewing] = useState<Delivery | null>(null);
  const totalBytes = useMemo(() => deliveries.reduce((n, d) => n + d.bytes, 0), [deliveries]);

  return (
    <div className="settings-section">
      <BackButton onClick={onBack} label="Drive Backups" />

      <div className="db-detail-header">
        <div className="db-user">
          <h2>{backup?.name || backup?.email || ownerId}</h2>
          {backup?.email && <span className="db-user-sub">{backup.email}</span>}
        </div>
        {backup && <BackupStateBadge row={backup} />}
      </div>

      <dl className="db-detail-facts">
        <div><dt>Google account</dt><dd>{backup?.accountEmail ?? '—'}</dd></div>
        <div><dt>Files</dt><dd>{deliveries.length} · {formatBytes(totalBytes)}</dd></div>
        <div><dt>Last delivered</dt><dd>{formatWhen(backup?.lastDeliveredAt)}</dd></div>
        <div><dt>Last swept</dt><dd>{formatWhen(backup?.lastSweepAt)}</dd></div>
        <div><dt>Connected</dt><dd>{backup ? new Date(backup.connectedAt).toLocaleDateString() : '—'}</dd></div>
      </dl>

      {backup?.problem && <p className="error">{backup.problem}</p>}
      {error && <p className="error">{error instanceof Error ? error.message : 'Failed to load deliveries'}</p>}

      <TabStrip
        tabs={VIEW_TABS}
        active={view}
        onChange={v => setParam('backupView', v === 'table' ? '' : v, true)}
        ariaLabel="Delivery views"
        className="db-detail-tabs"
      />

      {view === 'table'
        ? <DeliveryTable ownerId={ownerId} onOpen={setPreviewing} />
        : (
          <FolderBrowser
            deliveries={deliveries}
            loading={isLoading}
            path={folderPath}
            onNavigate={p => setParam('backupPath', p)}
            onOpen={setPreviewing}
          />
        )}

      {previewing && (
        <SourcePreview ownerId={ownerId} delivery={previewing} onClose={() => setPreviewing(null)} />
      )}
    </div>
  );
}

/** What the server filters on — see the deliveries endpoint. */
interface DeliveryFilters { q?: string; type?: string; userId?: string; from?: string; to?: string }
const NO_FILTERS: DeliveryFilters = {};

/** Labels for the server's `fileType` keys (connections/backup/fileTypes.ts). */
const FILE_TYPE_LABELS: Record<string, string> = {
  'question-paper': 'Question paper',
  'mark-scheme': 'Mark scheme',
  'answer-script': 'Answer script (PDF)',
  'answer-page': 'Answer page',
  'screen-working': 'On-screen working',
  report: 'Report',
  other: 'Other',
};

/** Every delivery as one flat, sortable table, filtered on the server.
 *
 *  Filters apply on Fetch: each returned row costs the server a blob HEAD for
 *  its upload time, so a query per keystroke would be the wrong trade.
 *
 *  Deliberately no Drive file id or link: we cannot open the user's file, and
 *  showing something that looks like a link to a person's private document is
 *  worse than showing nothing. */
function DeliveryTable({ ownerId, onOpen }: { ownerId: string; onOpen: (d: Delivery) => void }) {
  const [applied, setApplied] = useState<DeliveryFilters>(NO_FILTERS);
  const [pickedUser, setPickedUser] = useState<{ userId: string; label: string } | null>(null);
  const [userText, setUserText] = useState('');
  const [sort, setSort] = useState<SortState>({ sortBy: 'delivered', sortOrder: 'desc' });

  const { data, isFetching, error, refetch } = useQuery({
    queryKey: ['driveBackupDeliveries', ownerId, applied],
    queryFn: () => adminApi.getDriveBackupDeliveries(ownerId, applied),
  });
  const rows = data?.deliveries ?? [];

  const filterDefs: FilterDef[] = [
    {
      key: 'type', kind: 'select', placeholder: 'All types',
      options: Object.entries(FILE_TYPE_LABELS).map(([value, label]) => ({ value, label })),
    },
    { key: 'delivered', kind: 'daterange', label: 'Delivered' },
  ];

  const columns: ColumnDef<Delivery>[] = [
    {
      key: 'file', header: 'File', sortable: true, mobile: 'title',
      sortValue: d => d.driveName.toLowerCase(),
      render: d => d.driveName,
    },
    {
      key: 'type', header: 'Type', sortable: true, mobile: 'meta',
      sortValue: d => FILE_TYPE_LABELS[d.fileType] ?? d.fileType,
      render: d => FILE_TYPE_LABELS[d.fileType] ?? d.fileType,
    },
    {
      // Whose work: the student, for a teacher's copy of their work.
      key: 'user', header: 'User', sortable: true, mobile: 'meta',
      sortValue: d => (d.subjectName ?? '').toLowerCase(),
      render: d => d.subjectName ?? '—',
    },
    {
      key: 'folder', header: 'Folder', sortable: true, mobile: 'meta',
      sortValue: d => folderOf(d).toLowerCase(),
      render: d => <span className="db-folder-path">{folderOf(d)}</span>,
    },
    {
      key: 'size', header: 'Size', sortable: true, mobile: 'meta',
      sortValue: d => d.bytes,
      render: d => formatBytes(d.bytes),
    },
    {
      // The full blob path is a long GUID trail that wrapped over three lines
      // and pushed every other column narrow. Its tail is what tells two apart;
      // the whole path is a hover or a click (to copy) away.
      key: 'blob', header: 'Source blob', mobile: 'hide', width: '20ch',
      render: d => (d.blobPath
        ? <TruncatedValue value={d.blobPath} short={blobTail(d.blobPath)} className="db-blob-path" />
        : '—'),
    },
    {
      // When the source was uploaded to BuzzBrain, as against when we copied
      // it to Drive — a backfill delivers months-old work on one afternoon.
      key: 'uploaded', header: 'Uploaded', sortable: true, mobile: 'meta',
      sortValue: d => d.blobCreatedAt ?? '',
      render: d => uploadedWhen(d),
    },
    {
      key: 'delivered', header: 'Delivered', sortable: true, mobile: 'accessory',
      sortValue: d => d.deliveredAt,
      render: d => shortWhen(d.deliveredAt),
    },
  ];

  return (
    <>
      <SearchFilterBar
        filters={filterDefs}
        values={{
          search: applied.q ?? '', type: applied.type ?? '',
          deliveredFrom: applied.from ?? '', deliveredTo: applied.to ?? '',
        }}
        onChange={v => setApplied({
          q: v.search || undefined,
          type: v.type || undefined,
          from: v.deliveredFrom || undefined,
          to: v.deliveredTo || undefined,
          // The picker sits outside the bar's draft; it applies with the rest.
          userId: userText.trim() ? pickedUser?.userId : undefined,
        })}
        onFetch={() => refetch()}
        loading={isFetching}
        searchPlaceholder="File or folder name…"
        leading={(
          <ScopedUserSearchFilter
            value={userText}
            onChange={text => { setUserText(text); if (!text.trim()) setPickedUser(null); }}
            onPick={(o: UserSearchOption | null) => {
              setPickedUser(o ? { userId: o.userId, label: o.name || o.email } : null);
              setUserText(o ? (o.name || o.email) : '');
            }}
            placeholder="Whose work — name or email…"
          />
        )}
      />
      {error && <p className="error">{error instanceof Error ? error.message : 'Failed to load deliveries'}</p>}
      <p className="db-result-count">{isFetching ? 'Loading…' : `${rows.length} file${rows.length === 1 ? '' : 's'}`}</p>
      <DataTable<Delivery>
        columns={columns}
        data={rows}
        rowKey={d => d.artifactKey}
        onRowClick={onOpen}
        rowClickable={d => !!d.blobPath}
        sort={sort}
        onSortChange={setSort}
        compact
        bordered
        mobileCards
        loading={isFetching && !data}
        emptyMessage={applied === NO_FILTERS ? 'Nothing delivered yet.' : 'No files match.'}
      />
    </>
  );
}

type FolderEntry =
  | { kind: 'folder'; name: string; path: string; files: number; bytes: number; lastDeliveredAt: string }
  | { kind: 'file'; name: string; delivery: Delivery };

/** The immediate children of `path`: sub-folders rolled up (file count, size,
 *  newest delivery anywhere beneath), then the files that sit directly in it. */
function entriesAt(deliveries: Delivery[], path: string): FolderEntry[] {
  const prefix = splitPath(path);
  const folders = new Map<string, Extract<FolderEntry, { kind: 'folder' }>>();
  const files: FolderEntry[] = [];

  for (const d of deliveries) {
    const segs = splitPath(folderOf(d));
    if (!prefix.every((p, i) => segs[i] === p)) continue;
    if (segs.length === prefix.length) {
      files.push({ kind: 'file', name: d.driveName, delivery: d });
      continue;
    }
    const name = segs[prefix.length];
    const existing = folders.get(name);
    if (existing) {
      existing.files += 1;
      existing.bytes += d.bytes;
      if (d.deliveredAt > existing.lastDeliveredAt) existing.lastDeliveredAt = d.deliveredAt;
    } else {
      folders.set(name, {
        kind: 'folder', name, path: [...prefix, name].join('/'),
        files: 1, bytes: d.bytes, lastDeliveredAt: d.deliveredAt,
      });
    }
  }
  return [...folders.values(), ...files];
}

/** Drive-style browsing: breadcrumb, then the current folder's contents.
 *  The path lives in the URL, so the browser's back button walks up the tree. */
function FolderBrowser({ deliveries, loading, path, onNavigate, onOpen }: {
  deliveries: Delivery[];
  loading: boolean;
  path: string;
  onNavigate: (path: string) => void;
  onOpen: (d: Delivery) => void;
}) {
  const [sort, setSort] = useState<SortState>({ sortBy: 'name', sortOrder: 'asc' });
  const entries = useMemo(() => entriesAt(deliveries, path), [deliveries, path]);
  const crumbs = splitPath(path);

  const columns: ColumnDef<FolderEntry>[] = [
    {
      key: 'name', header: 'Name', sortable: true, mobile: 'title',
      // Folders stay above files whichever way the name sorts, as every file
      // browser does — the prefix keeps them grouped.
      sortValue: e => `${e.kind === 'folder' ? 0 : 1}${e.name.toLowerCase()}`,
      render: e => (
        <span className={`db-entry db-entry--${e.kind}`}>
          <span className="db-entry-icon" aria-hidden>{e.kind === 'folder' ? '📁' : '📄'}</span>
          {e.name}
        </span>
      ),
    },
    {
      key: 'files', header: 'Files', sortable: true, mobile: 'meta',
      sortValue: e => (e.kind === 'folder' ? e.files : 0),
      render: e => (e.kind === 'folder' ? e.files : ''),
    },
    {
      key: 'size', header: 'Size', sortable: true, mobile: 'meta',
      sortValue: e => (e.kind === 'folder' ? e.bytes : e.delivery.bytes),
      render: e => formatBytes(e.kind === 'folder' ? e.bytes : e.delivery.bytes),
    },
    {
      key: 'uploaded', header: 'Uploaded', sortable: true, mobile: 'meta',
      sortValue: e => (e.kind === 'file' ? e.delivery.blobCreatedAt ?? '' : ''),
      render: e => (e.kind === 'file' ? uploadedWhen(e.delivery) : ''),
    },
    {
      key: 'delivered', header: 'Last delivered', sortable: true, mobile: 'accessory',
      sortValue: e => (e.kind === 'folder' ? e.lastDeliveredAt : e.delivery.deliveredAt),
      render: e => shortWhen(e.kind === 'folder' ? e.lastDeliveredAt : e.delivery.deliveredAt),
    },
    {
      key: 'blob', header: 'Source blob', mobile: 'hide', width: '20ch',
      render: e => (e.kind === 'file' && e.delivery.blobPath
        ? <TruncatedValue value={e.delivery.blobPath} short={blobTail(e.delivery.blobPath)} className="db-blob-path" />
        : ''),
    },
  ];

  return (
    <>
      <nav className="db-breadcrumb" aria-label="Folder path">
        <button type="button" onClick={() => onNavigate('')} disabled={crumbs.length === 0}>My Drive</button>
        {crumbs.map((c, i) => (
          <span key={i}>
            <span className="db-breadcrumb-sep">/</span>
            <button
              type="button"
              onClick={() => onNavigate(crumbs.slice(0, i + 1).join('/'))}
              disabled={i === crumbs.length - 1}
            >
              {c}
            </button>
          </span>
        ))}
      </nav>
      <DataTable<FolderEntry>
        columns={columns}
        data={entries}
        rowKey={e => (e.kind === 'folder' ? `d:${e.path}` : `f:${e.delivery.artifactKey}`)}
        onRowClick={e => (e.kind === 'folder' ? onNavigate(e.path) : onOpen(e.delivery))}
        rowClickable={e => e.kind === 'folder' || !!e.delivery.blobPath}
        sort={sort}
        onSortChange={setSort}
        compact
        bordered
        mobileCards
        loading={loading}
        emptyMessage={deliveries.length === 0 ? 'Nothing delivered yet.' : 'This folder is empty.'}
      />
    </>
  );
}

/**
 * What one delivered file was copied FROM — our blob, not their Drive copy,
 * which we cannot read. Same bytes unless the blob was edited afterwards (a
 * page turned in the page grid, say), so the header says which it is.
 *
 * Images inline; a PDF in the browser's own viewer; anything else (a .docx
 * paper) as a download, since there is nothing useful to render.
 */
function SourcePreview({ ownerId, delivery, onClose }: {
  ownerId: string;
  delivery: Delivery;
  onClose: () => void;
}) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['driveBackupSource', ownerId, delivery.artifactKey],
    queryFn: () => adminApi.getDriveBackupSource(ownerId, delivery.artifactKey),
    staleTime: Infinity,
  });

  // A blob: URL rather than a data: URL — browsers refuse to render a PDF from
  // a data: URL in an iframe.
  const url = useMemo(() => {
    if (!data) return undefined;
    const bin = atob(data.dataBase64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return URL.createObjectURL(new Blob([bytes], { type: data.contentType }));
  }, [data]);
  useEffect(() => () => { if (url) URL.revokeObjectURL(url); }, [url]);

  const type = data?.contentType ?? '';
  return (
    <Modal isOpen onClose={onClose} title={delivery.driveName} size="xl" closeOnOverlayClick closeOnEscape>
      <p className="db-preview-meta">
        {folderOf(delivery)} · {formatBytes(delivery.bytes)} · uploaded {uploadedWhen(delivery)}
        {' '}· delivered {shortWhen(delivery.deliveredAt)}
        <br />
        <span className="db-blob-path">Source: {delivery.blobPath}</span>
      </p>
      {isLoading && <p>Loading…</p>}
      {error && <p className="error">{error instanceof Error ? error.message : 'Could not load the source file'}</p>}
      {url && type.startsWith('image/') && <img className="db-preview-image" src={url} alt={delivery.driveName} />}
      {url && type === 'application/pdf' && <iframe className="db-preview-pdf" src={url} title={delivery.driveName} />}
      {url && !type.startsWith('image/') && type !== 'application/pdf' && (
        <a href={url} download={delivery.driveName}>Download {delivery.driveName}</a>
      )}
    </Modal>
  );
}
